import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  FormLabel,
  Switch,
  RadioGroup,
  Radio,
  Divider,
  CircularProgress,
  Alert,
  LinearProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  SwapHoriz as ReplaceIcon,
  CloudUpload as UploadIcon,
  AttachFile as FileIcon,
} from '@mui/icons-material';
import { contentService } from '../../services';
import { uploadDirectService } from '../../services/upload-direct.service';

type ContentStatus = 'draft' | 'private' | 'public' | 'archived';
type VersionMode = 'add' | 'replace';
type AiTagMode = 'off' | 'merge' | 'replace';

interface CreateVersionDialogProps {
  open: boolean;
  contentId: string;
  organizationId: string;
  teamId: string;
  currentVersionNumber: number;
  /** Status of the currently active version — Replace Current inherits this. */
  currentVersionStatus?: ContentStatus;
  /** MIME type of the existing content — restricts file selection to the same type. */
  contentMimeType?: string;
  onClose: () => void;
  onCreated: (versionNumber: number) => void;
}

const STATUS_OPTIONS: { value: ContentStatus; label: string }[] = [
  { value: 'draft',    label: 'Draft — only visible to publishers' },
  { value: 'private',  label: 'Private — visible to team members' },
  { value: 'public',   label: 'Public — visible to all readers' },
  { value: 'archived', label: 'Archived — hidden from search' },
];

const STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Draft',
  private: 'Private',
  public: 'Public',
  archived: 'Archived',
};

const CreateVersionDialog: React.FC<CreateVersionDialogProps> = ({
  open,
  contentId,
  organizationId,
  teamId,
  currentVersionNumber,
  currentVersionStatus,
  contentMimeType,
  onClose,
  onCreated,
}) => {
  const [mode, setMode] = useState<VersionMode>('add');
  const [status, setStatus] = useState<ContentStatus>('draft');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useAiTitle, setUseAiTitle] = useState(false);
  const [useAiDescription, setUseAiDescription] = useState(true);
  const [aiTagMode, setAiTagMode] = useState<AiTagMode>('merge');

  const hasPriorVersion = currentVersionNumber > 0;
  const isReplacing = mode === 'replace' && hasPriorVersion;

  /** Returns an error string if the file's MIME type doesn't match the required type, or null if OK. */
  const validateMimeType = (file: File): string | null => {
    if (!contentMimeType) return null;
    const fileMime = file.type || 'application/octet-stream';
    if (fileMime === 'application/octet-stream') return null; // browser couldn't detect — let backend decide
    if (fileMime !== contentMimeType) {
      const ext = file.name.split('.').pop()?.toUpperCase() ?? 'this file type';
      return `Cannot use a ${ext} file here. This content requires ${contentMimeType} files.`;
    }
    return null;
  };

  const reset = () => {
    setMode('add');
    setStatus('draft');
    setSelectedFile(null);
    setUploadProgress(0);
    setIsDragOver(false);
    setError(null);
    setUseAiTitle(false);
    setUseAiDescription(true);
    setAiTagMode('merge');
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const mimeError = validateMimeType(file);
    if (mimeError) { setError(mimeError); return; }
    setError(null);
    setSelectedFile(file);
  }, [contentMimeType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mimeError = validateMimeType(file);
    if (mimeError) { setError(mimeError); e.target.value = ''; return; }
    setError(null);
    setSelectedFile(file);
    e.target.value = '';
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setSaving(true);
    setError(null);
    setUploadProgress(0);

    try {
      // 1. Upload the file
      const storageKey = await uploadDirectService.uploadVersionFile(
        contentId,
        teamId,
        selectedFile,
        (pct) => setUploadProgress(pct),
      );

      // 2. Create the new version record.
      // When replacing, inherit the current version's status so access level is preserved.
      const effectiveStatus = isReplacing && currentVersionStatus ? currentVersionStatus : status;
      const newVersion = await contentService.createVersion(contentId, {
        storageKey,
        fileSizeBytes: selectedFile.size,
        mimeType: selectedFile.type || 'application/octet-stream',
        status: effectiveStatus,
        organizationId,
        teamId,
        useAiTitle,
        useAiDescription,
        aiTagMode,
      });

      const newVersionNumber = newVersion?.versionNumber ?? newVersion?.version_number;

      // 3. If replacing, hard-delete the version that was current before this one
      if (isReplacing && currentVersionNumber > 0) {
        try {
          await contentService.deleteVersion(contentId, currentVersionNumber, teamId);
        } catch {
          // Non-fatal: new version is created; old file deletion failed silently
        }
      }

      onCreated(newVersionNumber);
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create version. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload New Version</DialogTitle>

      <DialogContent>
        <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Mode selector — only shown when a prior version exists */}
          {hasPriorVersion && (
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, newMode) => { if (newMode) setMode(newMode as VersionMode); }}
              size="small"
              fullWidth
              disabled={saving}
            >
              <ToggleButton value="add" sx={{ gap: 0.75 }}>
                <AddIcon fontSize="small" /> Add New Version
              </ToggleButton>
              <ToggleButton value="replace" sx={{ gap: 0.75 }}>
                <ReplaceIcon fontSize="small" /> Replace Current
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* File upload zone */}
          <Paper
            variant="outlined"
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleFileDrop}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: saving ? 'default' : 'pointer',
              bgcolor: isDragOver ? 'action.hover' : 'background.paper',
              borderStyle: isDragOver ? 'dashed' : 'solid',
              borderColor: isDragOver ? 'primary.main' : selectedFile ? 'primary.main' : 'divider',
              transition: 'all 0.2s',
            }}
            onClick={() => !saving && document.getElementById('version-file-input')?.click()}
          >
            <input
              id="version-file-input"
              type="file"
              hidden
              accept={contentMimeType || undefined}
              onChange={handleFileSelect}
              disabled={saving}
            />
            {selectedFile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <FileIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  {selectedFile.name} ({formatBytes(selectedFile.size)})
                </Typography>
                {!saving && (
                  <Button
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <UploadIcon color="action" />
                <Typography variant="body2" color="text.secondary">
                  Drag & drop or click to select a file
                </Typography>
                {contentMimeType && (
                  <Typography variant="caption" color="text.disabled">
                    Must be {contentMimeType}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {saving && selectedFile && uploadProgress < 100 && (
            <Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary">
                Uploading… {uploadProgress}%
              </Typography>
            </Box>
          )}

          {/* AI Enrichment Options */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              AI Enrichment Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={useAiTitle}
                  onChange={(e) => setUseAiTitle(e.target.checked)}
                  disabled={saving}
                  size="small"
                />
              }
              label="Replace title with AI-suggested title"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={useAiDescription}
                  onChange={(e) => setUseAiDescription(e.target.checked)}
                  disabled={saving}
                  size="small"
                />
              }
              label="Replace description with AI-generated description"
            />
            <Divider sx={{ my: 1 }} />
            <FormControl component="fieldset" disabled={saving}>
              <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>Tag application mode</FormLabel>
              <RadioGroup
                row
                value={aiTagMode}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'off' || v === 'merge' || v === 'replace') setAiTagMode(v);
                }}
              >
                <FormControlLabel value="off" control={<Radio size="small" />} label="Off" />
                <FormControlLabel value="merge" control={<Radio size="small" />} label="Merge" />
                <FormControlLabel value="replace" control={<Radio size="small" />} label="Replace" />
              </RadioGroup>
            </FormControl>
            {(useAiTitle || useAiDescription || aiTagMode !== 'off') && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                AI enrichment runs after upload.
                {(useAiTitle || useAiDescription) && (
                  <> The {[useAiTitle && 'title', useAiDescription && 'description'].filter(Boolean).join(' and ')} will be updated once processing completes.</>
                )}
                {aiTagMode !== 'off' && (
                  <> Tags will be {aiTagMode === 'replace' ? 'replaced by' : 'merged with'} AI suggestions.</>
                )}
              </Typography>
            )}
          </Paper>

          {/* Status selector (Add mode) or replace summary (Replace mode) */}
          {isReplacing ? (
            <Alert severity="warning">
              The uploaded file will replace the current version and inherit its status:{' '}
              <strong>{currentVersionStatus ? STATUS_LABELS[currentVersionStatus] : 'current'}</strong>.
              The current version's file will be permanently deleted.
            </Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="version-status-label">Status</InputLabel>
              <Select
                labelId="version-status-label"
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value as ContentStatus)}
                disabled={saving}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color={isReplacing ? 'warning' : 'primary'}
          disabled={saving || !selectedFile}
          startIcon={saving ? <CircularProgress size={16} /> : isReplacing ? <ReplaceIcon /> : <AddIcon />}
          onClick={handleSubmit}
        >
          {saving
            ? (uploadProgress < 100 ? 'Uploading…' : 'Saving…')
            : isReplacing ? 'Replace Current' : 'Add New Version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateVersionDialog;
